import { Phone, MapPin } from 'lucide-react';
import { useLanguageContext } from '../context/languageContext';
// The same mark the browser tab uses (public/click-logo.svg), so the banner
// and the page icon can never drift apart.
import clickLogo from '../assets/images/clickapp-logo.svg';
import { telHref } from '../utils/telHref';
// Shared with PartnerModal so the number and the services stay in step.
import { CLICK, CLICK_SERVICES as services } from '../data/click';

export default function Partners() {
  const { t } = useLanguageContext();

  return (
    <section id="partners" className="py-12 sm:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-6 md:mb-10">
          <div className="inline-block px-4 py-1.5 border border-gold/30 rounded-full mb-3">
            <span className="text-xs font-bold text-gold-deep tracking-widest uppercase">
              {t('Our Partner')}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-dark-chocolate px-2">
            {t('Powered by')} <span className="italic gradient-text-gold-deep">Click Albania</span>
          </h2>
        </div>

        {/* Showcase card — Click Albania's own navy/blue, ringed in gold so it still
            belongs to this site. Left-aligned and horizontal rather than a tall
            centred stack. */}
        <div className="max-w-5xl mx-auto relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-gold/25 bg-gradient-to-br from-click-navy via-[#0d3570] to-[#123f8c]">
          <div className="absolute -top-20 -right-10 w-64 h-64 bg-click-blue/25 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent pointer-events-none" />

          <div className="relative z-10 p-5 sm:p-8 md:p-10">
            {/* Lockup + CTA on one line */}
            <div className="flex items-center gap-4 md:gap-5">
              <div className="bg-white rounded-xl p-2 md:p-2.5 shadow-lg shadow-black/20 shrink-0">
                <img
                  src={clickLogo}
                  alt="Click Albania"
                  width="64"
                  height="64"
                  className="w-12 h-12 md:w-16 md:h-16 object-contain"
                />
              </div>

              <div className="min-w-0 flex-1">
                <p className="font-serif text-xl md:text-3xl font-bold text-white leading-tight">
                  Click Albania
                </p>
                {/* Location is repeated in the contact strip below, so it only
                    earns its place here once there is room for one clean line. */}
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.18em] text-click-blue mt-0.5">
                  {t('Software Company')}
                  <span className="hidden sm:inline">
                    <span className="text-white/25 mx-2">◆</span>
                    <span className="text-gold">{t('Durrës, Albania')}</span>
                  </span>
                </p>
              </div>

              <a
                href={telHref(CLICK.phone)}
                className="hidden sm:inline-flex items-center gap-2 bg-gold text-dark-chocolate font-bold text-xs md:text-sm py-3 px-5 md:px-6 rounded-xl hover:bg-white transition-colors duration-300 tracking-wide whitespace-nowrap shrink-0"
              >
                <Phone className="w-4 h-4 shrink-0" />
                {CLICK.phone}
              </a>
            </div>

            <p className="text-xs md:text-sm text-blue-100/75 font-light leading-relaxed mt-4 md:mt-5 max-w-3xl">
              {t('This website and the digital menu you are browsing were designed and built by Click Albania.')}
            </p>

            {/* Services as compact rows, not oversized cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3 md:gap-4 mt-5 md:mt-7 pt-5 md:pt-7 border-t border-white/10">
              {services.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-2.5 group">
                  <Icon className="w-4 h-4 md:w-[18px] md:h-[18px] text-gold shrink-0 mt-0.5" />
                  <span className="text-[11px] md:text-sm text-white/90 leading-snug group-hover:text-gold transition-colors duration-300">
                    {t(label)}
                  </span>
                </div>
              ))}
            </div>

            {/* Contact strip — the number lives in the call button now, so only
                the location is left here. */}
            <div className="mt-5 md:mt-7 pt-4 md:pt-5 border-t border-white/10 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] md:text-xs text-blue-100/60">
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gold shrink-0" />
                {t('Durrës, Albania')}
              </span>
            </div>

            {/* Phone-only CTA, full width where the inline one is hidden */}
            <a
              href={telHref(CLICK.phone)}
              className="sm:hidden mt-5 flex items-center justify-center gap-2 bg-gold text-dark-chocolate font-bold text-xs py-3 rounded-xl hover:bg-white transition-colors duration-300 tracking-wide"
            >
              <Phone className="w-4 h-4 shrink-0" />
              {CLICK.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
